import React, { useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import { formatGBP } from "../../lib/api";

/**
 * SideElevationView — full-screen wall view of one side. Multi-surface aware:
 * each surface renders as its own horizontal strip stacked vertically (bottom = pitch-side fence,
 * top = stand roof). Banners render at exact metre positions with bespoke widths AND heights.
 * Gaps between banners show as dashed "Available · 2.4m" placeholders for instant sales prompt.
 */
const SIDE_LABELS = { N: "North", E: "East", S: "South", W: "West" };

// Strip stack order, top-of-screen → bottom-of-screen (highest physical surface first)
const STACK_ORDER = ["stand_roof", "stand_back", "building_wall", "back_wall", "pitch_side_fence"];
const SURFACE_LABELS = {
    pitch_side_fence: "Pitch-side fence",
    back_wall: "Back perimeter wall",
    stand_back: "Stand back wall",
    stand_roof: "Stand roof strip",
    building_wall: "Building / clubhouse wall",
};

export default function SideElevationView({ side, slots, ground, assignments, sponsors, onAssign, onUnassign, onClose }) {
    const [dragSponsorId, setDragSponsorId] = useState(null);
    const [hoverSlotKey, setHoverSlotKey] = useState(null);
    const [filter, setFilter] = useState("");

    const isCustom = !!ground;

    // Group slots by their (side, surface_id) — works for both v2 ground and legacy default template.
    const surfaceGroups = useMemo(() => {
        const sideLower = side.toLowerCase();
        const filtered = slots.filter((s) => {
            if (s.category !== "pitchside" && s.category !== "gap") return false;
            const meta = s.meta || {};
            if (meta.side) return meta.side === side;
            return s.key.startsWith(`pb_${sideLower}`);
        });
        if (isCustom) {
            // Group by surface_id, look up surface info from ground spec.
            const sideGround = (ground.sides || []).find((s) => s.side === side);
            const surfaceInfo = {};
            (sideGround?.surfaces || []).forEach((surf) => { surfaceInfo[surf.id] = surf; });
            const groups = {};
            for (const s of filtered) {
                const sid = s.meta?.surface_id;
                if (!sid) continue;
                if (!groups[sid]) {
                    const info = surfaceInfo[sid] || {};
                    groups[sid] = {
                        id: sid, type: info.type || s.meta?.surface_type || "pitch_side_fence",
                        name: info.name || s.meta?.surface_label || SURFACE_LABELS[info.type || "pitch_side_fence"],
                        width_m: info.width_m || 24, height_m: info.height_m || 0.6, slots: [],
                    };
                }
                groups[sid].slots.push(s);
            }
            // Sort slots within each surface by their position_m
            Object.values(groups).forEach((g) => g.slots.sort((a, b) => (a.meta?.position_m || 0) - (b.meta?.position_m || 0)));
            // Sort surfaces by stack order (top first)
            return Object.values(groups).sort((a, b) => STACK_ORDER.indexOf(a.type) - STACK_ORDER.indexOf(b.type));
        }
        // Legacy: one group, all slots are 2.4m × 0.6m placeholders
        if (filtered.length === 0) return [];
        return [{
            id: "legacy", type: "pitch_side_fence", name: "Pitch-side fence",
            width_m: filtered.length * 2.4, height_m: 0.6,
            slots: filtered.sort((a, b) => (side === "E" || side === "W" ? (a.y || 0) - (b.y || 0) : (a.x || 0) - (b.x || 0))),
        }];
    }, [slots, side, ground, isCustom]);

    const totalWidthM = useMemo(() => surfaceGroups[0]?.width_m || 0, [surfaceGroups]);

    const assignedSponsorIds = useMemo(() => new Set(Object.values(assignments).map((a) => a.sponsor_id)), [assignments]);
    const benchSponsors = useMemo(() => sponsors
        .filter((s) => !assignedSponsorIds.has(s.id) && s.status !== "Cancelled")
        .filter((s) => !filter || s.name.toLowerCase().includes(filter.toLowerCase())),
        [sponsors, assignedSponsorIds, filter]);

    const handleDrop = async (slotKey, slotMeta) => {
        if (slotMeta?.kind === "gap") {
            alert("Gaps need a banner placed first — use the Ground Builder to add a banner here, then drag a sponsor onto it.");
            setDragSponsorId(null); setHoverSlotKey(null);
            return;
        }
        if (dragSponsorId) await onAssign(slotKey, dragSponsorId);
        setDragSponsorId(null); setHoverSlotKey(null);
    };

    return (
        <div className="fixed inset-0 z-50 bg-[#040605] overflow-auto" data-testid="side-elevation-view">
            {/* HEADER */}
            <div className="sticky top-0 z-10 bg-[#040605]/95 backdrop-blur border-b hairline">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Side elevation · wall view</div>
                        <h1 className="font-sport text-3xl md:text-4xl uppercase leading-none">{SIDE_LABELS[side]} side · the wall</h1>
                        <div className="text-[11px] text-zinc-400 mt-2 italic">
                            Standing on the pitch, looking at the {SIDE_LABELS[side].toLowerCase()} wall. Surfaces stacked top-to-bottom. Banners scaled to real W × H.
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Surfaces</div>
                            <div className="font-display font-extrabold text-2xl tracking-tightest">{surfaceGroups.length}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Total length</div>
                            <div className="font-display font-extrabold text-2xl tracking-tightest" style={{ color: "var(--tenant-primary)" }}>{totalWidthM.toFixed(1)}m</div>
                        </div>
                        <button onClick={onClose} className="border hairline px-3 py-2 text-xs uppercase tracking-ultra-wide text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 transition-colors" data-testid="elevation-close">
                            <X size={12} /> Close
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-1 xl:grid-cols-12 gap-6">
                <div className="xl:col-span-9">
                    {surfaceGroups.length === 0 ? (
                        <div className="border hairline p-12 text-center text-zinc-500 text-sm">
                            No surfaces on the {SIDE_LABELS[side].toLowerCase()} side. Switch to "Edit ground" mode to add one.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {surfaceGroups.map((surface) => (
                                <SurfaceStrip key={surface.id}
                                    surface={surface}
                                    assignments={assignments}
                                    hoverSlotKey={hoverSlotKey}
                                    dragSponsorId={dragSponsorId}
                                    onDragEnter={(k) => setHoverSlotKey(k)}
                                    onDragLeave={() => setHoverSlotKey(null)}
                                    onDrop={handleDrop}
                                    onUnassign={onUnassign} />
                            ))}
                            {/* Grass strip at the bottom */}
                            <div className="h-12" style={{ background: "linear-gradient(180deg, rgba(22,101,52,0.6) 0%, rgba(22,101,52,0.3) 100%)", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                <div className="text-[9px] uppercase tracking-ultra-wide text-white/50 px-6 pt-3">↑ The pitch</div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-3 gap-3 text-[11px] text-zinc-500">
                        <div className="border hairline px-3 py-2">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-600 mb-1">Filled banner</div>
                            <div>Solid tenant colour. Click × to clear.</div>
                        </div>
                        <div className="border hairline px-3 py-2">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-600 mb-1">Open banner</div>
                            <div>Dashed amber. Drag a sponsor to fill.</div>
                        </div>
                        <div className="border hairline px-3 py-2">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-600 mb-1">Available gap</div>
                            <div>Striped grey. Add a banner via the Ground Builder.</div>
                        </div>
                    </div>
                </div>

                {/* Bench */}
                <aside className="xl:col-span-3">
                    <div className="border hairline" data-testid="elevation-bench">
                        <div className="px-4 py-3 border-b hairline">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Bench</div>
                            <div className="font-display font-bold tracking-tight">Sponsors to place</div>
                        </div>
                        <div className="px-3 py-2 border-b hairline flex items-center gap-2">
                            <Search size={12} className="text-zinc-500" />
                            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…"
                                className="bg-transparent text-xs text-white placeholder:text-zinc-600 outline-none flex-1"
                                data-testid="elevation-filter" />
                        </div>
                        <div className="max-h-[60vh] overflow-auto">
                            {benchSponsors.length === 0 ? (
                                <div className="px-4 py-8 text-center text-xs text-zinc-500">No sponsors waiting. Add some from the Sponsors tab.</div>
                            ) : benchSponsors.map((s) => (
                                <div key={s.id} draggable
                                    onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/sponsor-id", s.id); setDragSponsorId(s.id); }}
                                    onDragEnd={() => { setDragSponsorId(null); setHoverSlotKey(null); }}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 border-b hairline last:border-b-0 cursor-grab active:cursor-grabbing hover:bg-white/[0.04] transition-colors ${dragSponsorId === s.id ? "opacity-40" : ""}`}
                                    data-testid={`elevation-bench-${s.id}`}>
                                    <div className="w-7 h-7 bg-white/[0.06] border hairline flex items-center justify-center text-[9px] font-bold text-zinc-300 shrink-0">
                                        {s.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-white truncate">{s.name}</div>
                                        <div className="text-[10px] text-zinc-500 truncate">{s.tier} · {formatGBP(s.monthly_gbp)}/mo</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

// ---------- Surface strip (one row per surface) ----------
const SurfaceStrip = ({ surface, assignments, hoverSlotKey, dragSponsorId, onDragEnter, onDragLeave, onDrop, onUnassign }) => {
    // Visual height scales with real height_m (so a 1.5m back wall is taller on screen than a 0.6m fence)
    const baseStripPx = 70; // px = 1m height
    const stripPx = Math.max(38, Math.min(180, surface.height_m * baseStripPx));

    return (
        <div className="border hairline overflow-hidden" data-testid={`surface-strip-${surface.id}`}>
            <div className="flex items-center justify-between px-3 py-1.5 border-b hairline bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-400">
                    {surface.name || SURFACE_LABELS[surface.type]}
                </div>
                <div className="text-[10px] text-zinc-500">{surface.width_m.toFixed(1)}m × {surface.height_m.toFixed(1)}m</div>
            </div>
            <div className="relative" style={{ height: stripPx, background: surface.type === "stand_roof" ? "rgba(180,30,30,0.04)" : surface.type === "building_wall" ? "rgba(180,90,30,0.04)" : "rgba(255,255,255,0.02)" }}>
                {/* Banners + gaps positioned proportionally */}
                {surface.slots.map((slot) => {
                    const meta = slot.meta || {};
                    const widthPct = (meta.width_m || 0) / surface.width_m * 100;
                    const leftPct = (meta.position_m || 0) / surface.width_m * 100;
                    // Banner height as % of surface height (clamped 25–100% for usability)
                    const heightPct = Math.max(25, Math.min(100, ((meta.height_m || surface.height_m) / surface.height_m) * 100));
                    return (
                        <BannerVisual key={slot.key} slot={slot}
                            assignment={assignments[slot.key]}
                            isHover={hoverSlotKey === slot.key}
                            isDragging={!!dragSponsorId}
                            leftPct={leftPct} widthPct={widthPct} heightPct={heightPct}
                            onDragEnter={() => onDragEnter(slot.key)}
                            onDragLeave={() => onDragLeave()}
                            onDrop={() => onDrop(slot.key, meta)}
                            onUnassign={onUnassign} />
                    );
                })}
                {/* Bottom rule (ground line) */}
                <div className="absolute inset-x-0 bottom-0 h-px bg-white/15" />
            </div>
        </div>
    );
};

const BannerVisual = ({ slot, assignment, isHover, isDragging, leftPct, widthPct, heightPct, onDragEnter, onDragLeave, onDrop, onUnassign }) => {
    const isGap = slot.category === "gap";
    const isFilled = !!assignment;
    const fill = isHover ? "var(--sb-amber)"
        : isFilled ? "var(--tenant-primary)"
            : isGap ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 12px)"
                : isDragging ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.04)";
    const stroke = isHover ? "var(--sb-amber)"
        : isFilled ? "var(--tenant-primary)"
            : isGap ? "rgba(255,255,255,0.18)" : "var(--sb-amber)";
    const dashed = isGap || (!isFilled && !isHover);
    return (
        <div
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={(e) => { e.preventDefault(); onDrop(); }}
            className="absolute bottom-0 transition-colors"
            style={{
                left: `${leftPct}%`, width: `${widthPct}%`, height: `${heightPct}%`,
                background: fill,
                border: `1.5px ${dashed ? "dashed" : "solid"} ${stroke}`,
                boxShadow: isHover ? "0 0 18px rgba(245,158,11,0.35)" : "none",
                minWidth: 16,
            }}
            data-testid={`elevation-slot-${slot.key}`}
        >
            <div className="flex items-center justify-center h-full px-1 text-center">
                {isFilled ? (
                    <div>
                        <div className="text-[10px] font-bold leading-tight" style={{ color: "var(--tenant-primary-foreground, #000)" }}>{assignment.sponsor_name}</div>
                        <div className="text-[9px] opacity-70" style={{ color: "var(--tenant-primary-foreground, #000)" }}>{(slot.meta?.width_m || 0).toFixed(1)}×{(slot.meta?.height_m || 0).toFixed(1)}m</div>
                    </div>
                ) : isGap ? (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/60">Gap</div>
                        <div className="text-[9px] text-white/40">{(slot.meta?.width_m || 0).toFixed(1)}m</div>
                    </div>
                ) : (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/70">Available</div>
                        <div className="text-[9px] text-white/40">{(slot.meta?.width_m || 0).toFixed(1)}×{(slot.meta?.height_m || 0).toFixed(1)}m</div>
                    </div>
                )}
            </div>
            {isFilled && (
                <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Clear this banner?")) onUnassign(slot.key); }}
                    className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                    title="Clear banner" data-testid={`elevation-unassign-${slot.key}`}>
                    <X size={9} className="text-white/80" />
                </button>
            )}
        </div>
    );
};
